import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Clock, User, MessageSquare, FileText, 
  History, Edit2, ArrowLeft, ChevronDown, Plus 
} from 'lucide-react';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const RiskDetailView = () => {
  const { riskCode } = useParams();
  const navigate = useNavigate();
  
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Fetch risk details
  useEffect(() => {
    const fetchRiskDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/risks.php?id=${riskCode}`);
        const data = await response.json();

        if (data.success) {
          setRisk(data.data);
        } else {
          throw new Error(data.error || 'Failed to fetch risk details');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (riskCode) {
      fetchRiskDetails();
    }
  }, [riskCode]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;

    try {
      setSubmittingComment(true);
      const response = await fetch(`${API_BASE_URL}/risk-comments.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          risk_id: risk.id,
          user_id: 1, // TODO: Replace with actual logged-in user ID
          comment: newComment
        }),
      });

      const data = await response.json();

      if (data.success) {
        setRisk(prev => ({
          ...prev,
          comments: [data.data, ...prev.comments]
        }));
        setNewComment('');
      } else {
        throw new Error(data.error || 'Failed to add comment');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Navigation handlers
  const handleBack = () => {
    navigate('/risks');
  };

  const handleEdit = () => {
    navigate(`/risks/${riskCode}/edit`);
  };
  const StatusBadge = ({ score, size = 'normal' }) => {
    const getStatusInfo = () => {
      const riskScore = parseInt(score);
      if (riskScore >= 16) return { style: 'high', label: 'High Risk' };
      if (riskScore >= 9) return { style: 'medium', label: 'Medium Risk' };
      return { style: 'low', label: 'Low Risk' };
    };

    const statusStyles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };

    const sizeClasses = {
      normal: 'px-3 py-1 text-sm',
      large: 'px-4 py-2 text-base'
    };

    const { style, label } = getStatusInfo();
    return (
      <span className={`inline-flex items-center rounded-full border ${statusStyles[style]} ${sizeClasses[size]} font-medium`}>
        {label}
      </span>
    );
  };

  // Approval status badge component
  const ApprovalBadge = ({ status }) => {
    const approvalStyles = {
      'pending-smt': 'bg-blue-100 text-blue-800 border-blue-200',
      'pending-gb': 'bg-purple-100 text-purple-800 border-purple-200',
      'approved': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200'
    };

    const approvalLabels = {
      'pending-smt': 'Pending SMT',
      'pending-gb': 'Pending GB',
      'approved': 'Approved',
      'rejected': 'Rejected'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${approvalStyles[status]}`}>
        {approvalLabels[status]}
      </span>
    );
  };
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-gray-500">Loading risk details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!risk) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-gray-500">Risk not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back button and header */}
      <div className="mb-6">
        <button 
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Risk Register
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold mb-2">{risk.title}</h1>
            <div className="flex items-center space-x-4">
              <StatusBadge score={risk.risk_score} size="large" />
              <ApprovalBadge status={risk.approval_status} />
              <span className="text-gray-500">ID: {risk.risk_code}</span>
            </div>
          </div>
          <button 
            onClick={handleEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Risk
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main content - 2 columns */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Description</h2>
            <p className="text-gray-700">{risk.description}</p>
          </div>

          {/* Risk Assessment */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Risk Assessment</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="text-sm text-gray-500 mb-1">Level</div>
                <div className="text-xl font-semibold">{risk.likelihood}/5</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="text-sm text-gray-500 mb-1">Impact</div>
                <div className="text-xl font-semibold">{risk.impact}/5</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="text-sm text-gray-500 mb-1">Risk Score</div>
                <div className="text-xl font-semibold">{risk.risk_score}/25</div>
              </div>
            </div>
          </div>

          {/* Mitigation and Progress */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Mitigation Strategy</h2>
            <p className="text-gray-700 mb-4">{risk.mitigation_strategy}</p>
            
            <h3 className="text-md font-semibold mb-2">Contingency Plan</h3>
            <p className="text-gray-700 mb-4">{risk.contingency_plan}</p>

            <h3 className="text-md font-semibold mb-2">Notes</h3>
            <p className="text-gray-700 mb-4">{risk.notes.nl2br()}</p>
          </div>

          {/* Comments Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Comments</h2>
            
            {/* Comment Form */}
            <form onSubmit={handleAddComment} className="mb-6">
              <div className="flex items-start space-x-4">
                <div className="flex-grow">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submittingComment ? 'Posting...' : 'Post'}
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {risk.comments?.map(comment => (
                <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium">{comment.user_name}</span>
                      <span className="text-gray-500 text-sm ml-2">{comment.role}</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700">{comment.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Details Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Category</dt>
                <dd className="mt-1">{risk.category_label}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Owner</dt>
                <dd className="mt-1 flex items-center">
                  <User className="w-4 h-4 mr-2 text-gray-400" />
                  {risk.owner_name}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Date Identified</dt>
                <dd className="mt-1 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  {new Date(risk.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Target Resolution</dt>
                <dd className="mt-1 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  {new Date(risk.target_resolution_date).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Last Updated</dt>
                <dd className="mt-1 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  {new Date(risk.updated_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* History Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">History</h2>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-gray-500 hover:text-gray-700"
              >
                <ChevronDown className={`w-5 h-5 transform ${showHistory ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {showHistory && (
              <div className="space-y-4">
                {risk.history?.map(event => (
                  <div key={event.id} className="border-l-2 border-gray-200 pl-4">
                    <div className="text-sm">
                      <span className="font-medium">{event.full_name}</span>
                      <span className="text-gray-500 ml-2">{event.action}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {new Date(event.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{event.details}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiskDetailView;
